import { useQuery } from "@tanstack/react-query"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { HOME } from "@/shared/constants/backend-api"

export interface LightweightHome {
  id: number
  address: string
  latitude: number | null
  longitude: number | null
  user: {
    fullname: string
    phone: string
  }
}

export interface LightweightHomesResponse {
  success: boolean
  data: LightweightHome[]
  message?: string
  error?: string
}

/**
 * Hook to fetch lightweight home data for dropdowns and lists
 * Returns only essential fields: id, address, latitude, longitude, user fullname, user phone
 * 
 * @param regionId - Optional region ID to filter homes by region
 * @returns {Object} {
 *  data: LightweightHomesResponse,
 *  isLoading: boolean,
 *  error: Error | null
 * }
 */
type UseLightweightHomesOptions = {
  enabled?: boolean
}

export function useLightweightHomes(regionId?: number, options: UseLightweightHomesOptions = {}) {
  const { enabled = true } = options
  const query = useQuery({
    queryKey: ['homes', 'lightweight', regionId],
    queryFn: async (): Promise<LightweightHomesResponse> => {
      const url = regionId
        ? `${HOME.base}/lightweight?regionId=${regionId}`
        : `${HOME.base}/lightweight`
      const res = await authFetchJson<LightweightHome[]>(url)
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch lightweight homes')
      }
      return {
        success: true,
        data: res.data
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    enabled,
  })

  return query
}
